import { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown, 
  Briefcase,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  ExternalLink,
  Globe,
  Target,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Circle,
  ArrowRight,
  HelpCircle,
  Lightbulb,
  BookOpen,
  Activity,
  Wifi,
  WifiOff,
  Zap,
  Shield,
  Brain
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DataCardSkeleton, InlineLoader, loadingMessages } from "@/components/LoadingIndicators";
import { DailyBrief } from "@/components/DailyBrief";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Traffic light component
const TrafficLight = ({ status, size = "md" }) => {
  const colors = {
    green: "bg-green-500",
    amber: "bg-amber-500", 
    red: "bg-red-500",
    grey: "bg-gray-400"
  };
  const sizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-6 h-6"
  };
  return (
    <div className={`${sizes[size]} ${colors[status]} rounded-full shadow-lg`} 
         style={{ boxShadow: `0 0 8px ${status === 'green' ? '#22c55e' : status === 'amber' ? '#f59e0b' : status === 'red' ? '#ef4444' : '#9ca3af'}` }} 
    />
  );
};

// Simple explanation tooltip
const WhyThis = ({ children, explanation }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help border-b border-dashed border-muted-foreground">
          {children}
          <HelpCircle className="w-3 h-3 text-muted-foreground" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-sm">{explanation}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const Dashboard = () => {
  const { isPaperTrading } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [loadingRegime, setLoadingRegime] = useState(true);
  const [loadingMacro, setLoadingMacro] = useState(true);
  const [hlPortfolios, setHlPortfolios] = useState([]);
  const [totalHlValue, setTotalHlValue] = useState(0);
  const [avgReturn, setAvgReturn] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [advice, setAdvice] = useState(null);
  
  // Real-time WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [livePrices, setLivePrices] = useState({});
  const [marketRegime, setMarketRegime] = useState(null);
  const [macroIntelligence, setMacroIntelligence] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Connect to WebSocket for live updates
  const connectWebSocket = useCallback(() => {
    if (!WS_BASE) return;
    
    try {
      const ws = new WebSocket(`${WS_BASE}/ws/portfolio`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'portfolio_update') {
            // Update portfolio metrics from WebSocket
            if (data.regime) {
              setMarketRegime(data.regime);
            }
          } else if (data.type === 'price_update') {
            setLivePrices(data.prices || {});
          }
        } catch (e) {
          console.error('WebSocket parse error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        // Don't aggressively reconnect in preview - use polling instead
        if (!window.location.hostname.includes('preview')) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, []);
  
  // Polling fallback for when WebSocket isn't available
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (!wsConnected) {
        fetchMarketRegime();
      }
    }, 60000); // Poll every 60 seconds when WebSocket is down
    
    return () => clearInterval(pollInterval);
  }, [wsConnected]);

  useEffect(() => {
    fetchDashboardData();
    fetchMarketRegime();
    fetchMacroIntelligence();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);
  
  const fetchMarketRegime = async () => {
    setLoadingRegime(true);
    try {
      const response = await axios.get(`${API}/market/regime`);
      setMarketRegime(response.data);
    } catch (error) {
      console.error("Error fetching market regime:", error);
    } finally {
      setLoadingRegime(false);
    }
  };
  
  const fetchMacroIntelligence = async () => {
    setLoadingMacro(true);
    try {
      const response = await axios.get(`${API}/market/intelligence`);
      setMacroIntelligence(response.data);
    } catch (error) {
      console.error("Error fetching macro intelligence:", error);
    } finally {
      setLoadingMacro(false);
    }
  };

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      const hlPortfoliosRes = await axios.get(`${API}/hl-portfolios`);
      const portfolios = hlPortfoliosRes.data.portfolios || [];
      setHlPortfolios(portfolios);
      
      const total = portfolios.reduce((sum, p) => sum + (p.total_value || 0), 0);
      setTotalHlValue(total);
      
      const weightedReturn = total > 0 
        ? portfolios.reduce((sum, p) => sum + (p.total_value || 0) * (p.total_return_pct || 0), 0) / total
        : 0;
      setAvgReturn(weightedReturn);
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getSimpleAdvice = async () => {
    setLoadingAdvice(true);
    try {
      const response = await axios.get(`${API}/portfolio/simple-advice`);
      setAdvice(response.data);
    } catch (error) {
      console.error("Error getting advice:", error);
    } finally {
      setLoadingAdvice(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  const targetROI = 20;
  const roiProgress = Math.min(100, Math.max(0, (avgReturn / targetROI) * 100));
  const overallHealth = avgReturn >= 25 ? 'green' : avgReturn >= 10 ? 'amber' : 'red';

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 animate-slide-up max-w-full overflow-x-hidden" data-testid="dashboard">
      {/* Header - Simple */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Your Money at a Glance
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Simple advice for your {hlPortfolios.length} investment portfolios
          </p>
        </div>
        <Button 
          onClick={getSimpleAdvice}
          disabled={loadingAdvice}
          size="default"
          className="text-sm md:text-base w-full sm:w-auto"
          className="text-base"
          data-testid="get-advice-btn"
        >
          {loadingAdvice ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Lightbulb className="w-5 h-5 mr-2" />}
          What Should I Do?
        </Button>
      </div>

      {/* Loading indicator for AI advice */}
      {loadingAdvice && (
        <Card className="border-2 border-primary/30 bg-primary/5">
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-primary/70" />
                </div>
              </div>
              <div className="text-center">
                <p className="font-medium text-lg">Generating Your Personalized Advice...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  AI is reviewing your portfolios and current market conditions
                </p>
              </div>
              <div className="w-64 mt-2">
                <Progress value={loadingAdvice ? undefined : 100} className="h-2 [&>div]:animate-[progress_2s_ease-in-out_infinite]" />
              </div>
              <p className="text-xs text-muted-foreground">This usually takes 15-30 seconds</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily AI Action Brief */}
      <DailyBrief />

      {/* Main Summary Card - Very Simple */}
      <Card className="gradient-glow">
        <CardContent className="pt-4 md:pt-6">
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="flex items-center gap-3 md:gap-4">
              <TrafficLight status={overallHealth} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="text-xs md:text-sm text-muted-foreground">Your Total Investments</p>
                <p className="text-2xl md:text-4xl font-bold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {formatCurrency(totalHlValue)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-6">
              <div className="flex-1 text-center">
                <p className="text-xs md:text-sm text-muted-foreground">Your Profit</p>
                <p className={`text-lg md:text-2xl font-bold ${avgReturn >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {avgReturn >= 0 ? '+' : ''}{avgReturn.toFixed(1)}%
                </p>
              </div>
              <div className="flex-1 text-center">
                <p className="text-xs md:text-sm text-muted-foreground">Target: 20%</p>
                <p className={`text-lg md:text-2xl font-bold ${avgReturn >= 20 ? 'text-green-500' : 'text-amber-500'}`}>
                  {avgReturn >= 20 ? '✓ Hit!' : `${(20 - avgReturn).toFixed(1)}% to go`}
                </p>
              </div>
            </div>
          </div>
          
          {/* Simple explanation */}
          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm">
              {overallHealth === 'green' && (
                <><span className="font-bold text-green-500">Great news!</span> Your investments are doing well. You've made good returns.</>
              )}
              {overallHealth === 'amber' && (
                <><span className="font-bold text-amber-500">Doing OK.</span> Your investments are growing, but there's room to improve.</>
              )}
              {overallHealth === 'red' && (
                <><span className="font-bold text-red-500">Needs attention.</span> Your investments aren't growing as much as they should.</>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Market Intelligence Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Market Regime Card */}
        <Card className="border-l-4 border-l-primary relative" data-testid="market-regime-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Market Weather
              {loadingRegime && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingRegime && !marketRegime ? (
              <DataCardSkeleton title="Analyzing Markets" lines={2} />
            ) : marketRegime ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <TrafficLight 
                    status={marketRegime.regime === 'BULL' ? 'green' : marketRegime.regime === 'BEAR' ? 'red' : 'amber'} 
                  />
                  <span className="font-bold text-lg">
                    {marketRegime.regime === 'BULL' ? '☀️ Sunny' : 
                     marketRegime.regime === 'BEAR' ? '🌧️ Stormy' : 
                     marketRegime.regime === 'VOLATILE' ? '⛈️ Unpredictable' : '☁️ Cloudy'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {marketRegime.regime === 'BULL' ? 'Good time to invest - markets are generally rising' :
                   marketRegime.regime === 'BEAR' ? 'Be careful - markets are falling' :
                   marketRegime.regime === 'VOLATILE' ? 'Markets are jumping around - be patient' :
                   'Markets are steady - no big changes'}
                </p>
                <div className="text-xs flex items-center gap-1 text-muted-foreground mt-2">
                  <Shield className="w-3 h-3" />
                  Fear Level: {marketRegime.vix_level?.toFixed(0) || 'N/A'}
                </div>
              </div>
            ) : (
              <Skeleton className="h-16" />
            )}
          </CardContent>
        </Card>

        {/* Macro Intelligence Card */}
        <Card className="border-l-4 border-l-blue-500 relative" data-testid="macro-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="w-4 h-4" />
              AI Market Insight
              {loadingMacro && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingMacro && !macroIntelligence ? (
              <DataCardSkeleton title="AI Analyzing..." lines={2} />
            ) : macroIntelligence ? (
              <div className="space-y-2">
                <p className="text-sm line-clamp-3">{macroIntelligence.outlook}</p>
                <Badge variant={
                  macroIntelligence.risk_level === 'Low' ? 'default' :
                  macroIntelligence.risk_level === 'Medium' ? 'secondary' : 'destructive'
                }>
                  Risk: {macroIntelligence.risk_level}
                </Badge>
              </div>
            ) : (
              <Skeleton className="h-16" />
            )}
          </CardContent>
        </Card>

        {/* Live Connection Status Card */}
        <Card className={`border-l-4 ${wsConnected ? 'border-l-green-500' : 'border-l-gray-500'}`} data-testid="live-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {wsConnected ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-gray-500" />}
              Live Updates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-blue-500 animate-pulse'}`} />
                <span className="text-sm">{wsConnected ? 'Live Feed' : 'API Polling'}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {wsConnected ? 'Real-time price updates active' : 'Updating data every minute'}
              </p>
              {wsConnected && Object.keys(livePrices).length > 0 && (
                <div className="text-xs text-green-500 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  {Object.keys(livePrices).length} prices tracked
                </div>
              )}
              {!wsConnected && marketRegime && (
                <div className="text-xs text-blue-500 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  Market data synced
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Portfolio Traffic Lights */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Your Portfolios - How Are They Doing?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {hlPortfolios
              .sort((a, b) => (b.total_return_pct || 0) - (a.total_return_pct || 0))
              .map((portfolio) => {
                const returnPct = portfolio.total_return_pct || 0;
                const status = returnPct >= 30 ? 'green' : returnPct >= 10 ? 'amber' : returnPct >= 0 ? 'amber' : 'red';
                return (
                  <div key={portfolio.id} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-4">
                      <TrafficLight status={status} />
                      <div>
                        <p className="font-medium">{portfolio.name}</p>
                        <p className="text-sm text-muted-foreground">{portfolio.holdings?.length || 0} investments</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(portfolio.total_value || 0)}</p>
                      <p className={`text-sm font-bold ${returnPct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}% profit
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
          
          {/* Legend */}
          <div className="mt-4 p-3 bg-muted/10 rounded-lg flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2"><TrafficLight status="green" size="sm" /> <span>Excellent (30%+)</span></div>
            <div className="flex items-center gap-2"><TrafficLight status="amber" size="sm" /> <span>OK (0-30%)</span></div>
            <div className="flex items-center gap-2"><TrafficLight status="red" size="sm" /> <span>Losing Money</span></div>
          </div>
        </CardContent>
      </Card>

      {/* AI Advice Panel - Simple Language */}
      {advice && (
        <Card className="border-2 border-primary/30 bg-primary/5" data-testid="advice-panel">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-primary" />
              <CardTitle className="text-xl">Here's What You Should Do</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Simple Summary */}
            <div className="p-4 bg-background rounded-lg">
              <p className="text-lg leading-relaxed">{advice.simple_summary}</p>
            </div>

            {/* Step by Step Actions */}
            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" /> Your Action Plan (Do These In Order)
              </h3>
              <div className="space-y-4">
                {advice.steps?.map((step, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                    step.urgency === 'now' ? 'border-l-red-500 bg-red-500/5' :
                    step.urgency === 'soon' ? 'border-l-amber-500 bg-amber-500/5' :
                    'border-l-green-500 bg-green-500/5'
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        step.urgency === 'now' ? 'bg-red-500' :
                        step.urgency === 'soon' ? 'bg-amber-500' :
                        'bg-green-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={
                            step.action === 'SELL' ? 'bg-red-500/20 text-red-500' :
                            step.action === 'BUY' ? 'bg-green-500/20 text-green-500' :
                            'bg-amber-500/20 text-amber-500'
                          }>
                            {step.action}
                          </Badge>
                          <span className="font-bold">{step.what}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{step.why_simple}</p>
                        
                        {/* How to do it */}
                        <div className="mt-3 p-3 bg-background/50 rounded text-sm">
                          <p className="font-medium mb-1">📋 How to do this:</p>
                          <p>{step.how_to}</p>
                        </div>

                        {/* What happens if you do/don't */}
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="p-2 bg-green-500/10 rounded">
                            <p className="font-medium text-green-600">✓ If you do this:</p>
                            <p>{step.if_you_do}</p>
                          </div>
                          <div className="p-2 bg-red-500/10 rounded">
                            <p className="font-medium text-red-600">✗ If you don't:</p>
                            <p>{step.if_you_dont}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Future Projection */}
            {advice.future_projection && (
              <div className="p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-lg">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" /> If You Follow This Advice
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <div className="text-center p-3 bg-background/50 rounded">
                    <p className="text-sm text-muted-foreground">Expected Return</p>
                    <p className="text-2xl font-bold text-green-500">{advice.future_projection.expected_return}</p>
                  </div>
                  <div className="text-center p-3 bg-background/50 rounded">
                    <p className="text-sm text-muted-foreground">Your Money In 1 Year</p>
                    <p className="text-2xl font-bold">{advice.future_projection.value_in_1_year}</p>
                  </div>
                  <div className="text-center p-3 bg-background/50 rounded">
                    <p className="text-sm text-muted-foreground">Risk Level</p>
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <TrafficLight status={advice.future_projection.risk_color} />
                      <span className="font-bold">{advice.future_projection.risk_level}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Simple Pie Chart Suggestion */}
            {advice.ideal_split && (
              <div className="p-4 bg-muted/20 rounded-lg">
                <h3 className="font-bold mb-3">How Your Money Should Be Split (Ideal Mix)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(advice.ideal_split).map(([category, pct]) => (
                    <div key={category} className="text-center p-3 bg-background rounded">
                      <p className="text-2xl font-bold text-primary">{pct}%</p>
                      <p className="text-sm text-muted-foreground">{category}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Call to Action if no advice yet */}
      {!advice && (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-16 h-16 mx-auto mb-4 text-primary/30" />
            <h3 className="text-xl font-bold mb-2">Want to Know What to Do Next?</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Click the button below and I'll look at all your investments and tell you 
              exactly what to do - in simple, plain English.
            </p>
            <Button size="lg" onClick={getSimpleAdvice} disabled={loadingAdvice}>
              {loadingAdvice ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Lightbulb className="w-5 h-5 mr-2" />}
              Tell Me What To Do
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Simple Footer */}
      <div className="text-center py-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          This is guidance based on your investments, not financial advice. 
          If you're unsure, speak to a financial advisor.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
